import { createAction, handleActions } from 'redux-actions';
import { StorageApi } from '../../utils/storageApi';
import { Api } from '../../api/api';
import { logout, onForbidden } from '../account/login';
import _ from 'lodash';
import {
    fetchGetOrgForApp,
    fetchGetOrgBalance,
    getOrgPermissions,
    fetchGetOperationAvailability
} from '../organization/organization';
import { cleanStartAccreditation } from '../accreditation/accreditations';
import { fetchGetPresets } from "../finance/finance";
import { fetchGetCurrencies } from "../finance/currencies";
import { getUserId } from './userData';

//- Constants
export const ROLES = {
    SYS_ADMIN: 'SysAdmin',
    EMPLOYEE: 'Employee',
    APPLICANT: 'Applicant'
};

export const NO_SUB_ROLES = 'No sub roles';
export const SUB_ROLES = {
    ANCHOR: 'Anchor',
    INSERTER: 'Inserter',
    VALIDATOR: 'Validator',
    HM: 'HRManager',
    BO: 'BusinessOwner',
    ROOT: 'SysAdmin_Root',
    FA: 'SysAdmin_FinancialManager',
    PA: 'SysAdmin_ProcessingManager'
};

export const ROLES_TRANSLATIONS = {
    [ROLES.SYS_ADMIN]: "admin_label",
    [ROLES.EMPLOYEE]: "employee_label",
    [ROLES.APPLICANT]: "applicant_label"
};

export const SUB_ROLES_TRANSLATIONS = {
    [SUB_ROLES.ANCHOR]: "anchor_label",
    [SUB_ROLES.INSERTER]: "inserter_label",
    [SUB_ROLES.VALIDATOR]: "validator_label",
    [SUB_ROLES.HM]: "hm_label",
    [SUB_ROLES.BO]: "bo_label"
};

const SUB_ROLES_REQUIRING_HAS_IVS = [SUB_ROLES.INSERTER, SUB_ROLES.VALIDATOR];

//- Actions
export const setRole = createAction('ROLE_SET');

export const setRoleAndSubRoles = (role, subRoles) => (dispatch) => {
    dispatch(setRole(role));
    if (ROLES[role] === ROLES.EMPLOYEE) {
        dispatch(setSubRoles(subRoles));
        dispatch(fetchGetOrgForApp());
        dispatch(fetchGetOperationAvailability());
    }
    if (ROLES[role] === ROLES.SYS_ADMIN) {
        dispatch(onSetSubRoles(subRoles));
        dispatch(fetchGetCurrencies());
        if (subRoles.includes(SUB_ROLES.ROOT) || subRoles.includes(SUB_ROLES.FA)) {
            dispatch(fetchGetPresets());
        }
    }
};

export const onSetSubRoles = createAction('SUB_ROLES_SET');

export const setSubRoles = (subRoles) => (dispatch, getState) => {
    subRoles = _.sortBy(subRoles);

    let orgPermissions = getOrgPermissions(getState());
    if (!_.isNil(orgPermissions) && !orgPermissions.hasIVs) {
        subRoles = subRoles.filter((subRole) => {
            return !SUB_ROLES_REQUIRING_HAS_IVS.includes(subRole);
        });
    }

    let lastUsedSubRole = StorageApi.getLastUsedSubRole();
    let subRole = subRoles.length > 0 ? subRoles[0] : NO_SUB_ROLES;

    if (subRole !== NO_SUB_ROLES){
        if (!_.isNil(lastUsedSubRole) && subRoles.includes(lastUsedSubRole)) {
            subRole = lastUsedSubRole;
        } else {
            StorageApi.setLastUsedSubRole(subRole);
        }
        if (subRole === SUB_ROLES.ANCHOR) {
            dispatch(fetchGetOrgBalance());
        } else {
            dispatch(fetchGetGroups(subRole));
        }
    }

    dispatch(onSetSubRoles(subRoles));
    dispatch(setCurrentSubRole(subRole));
};

export const fetchGetSubRolesFailed = createAction('FETCH_GET_SUB_ROLES_FAILED');
export const fetchGetSubRolesSuccess = createAction('FETCH_GET_SUB_ROLES_SUCCESS');
export const fetchGetSubRoles = createAction(
    'FETCH_GET_SUB_ROLES',
    () => Api.getUserData(),
    () => ({
        apiCall: true,
        onSuccess: onGetSubRolesSuccess,
        onFail: fetchGetSubRolesFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);
export const onGetSubRolesSuccess = (userData) => (dispatch) => {
    dispatch(setSubRoles(userData.subRoles));
    dispatch(fetchGetSubRolesSuccess());
};

export const fetchGetGroupsFailed = createAction('FETCH_GET_GROUPS_FAILED');
export const fetchGetGroupsSuccess = createAction('FETCH_GET_GROUPS_SUCCESS');
export const onGetGroupsSuccess = (groups) => (dispatch) => {
    if (groups.length === 0) {
        dispatch(fetchGetGroupsFailed());
        dispatch(setUserSubRoleMissing(true));
    } else {
        groups = _.sortBy(groups);
        let lastUsedGroup = StorageApi.getLastUsedGroup();
        let group = groups[0].id;
        if (!_.isNil(lastUsedGroup) && !_.isNil(groups.find((elem) => lastUsedGroup === elem.id))) {
            group = lastUsedGroup;
        }
        dispatch(fetchGetGroupsSuccess(groups));
        dispatch(changeCurrentGroup(group));
    }
};

export const fetchReGetGroups = () => (dispatch, getState) => {
    let subRole = getCurrentSubRole(getState());
    dispatch(fetchGetGroups(subRole));
};

export const fetchGetGroups = createAction(
    'FETCH_GET_GROUPS',
    (subRole) => {
        switch (subRole) {
            case SUB_ROLES.INSERTER:
                return Api.getInserterGroups();
            case SUB_ROLES.VALIDATOR:
                return Api.getValidatorGroups();
            case SUB_ROLES.HM:
                return Api.getHMGroups();
            case SUB_ROLES.BO:
                return Api.getBOGroups();
            default:
                break;
        }
    },
    () => ({
        apiCall: true,
        onSuccess: onGetGroupsSuccess,
        onFail: fetchGetGroupsFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const setCurrentSubRole = createAction('CURRENT_SUB_ROLE_SET', newSubRole => newSubRole);

export const changeCurrentSubRole = (newSubRole) => (dispatch, getState) => {
    let userId = StorageApi.getUserId();
    let userIdFromState = getUserId(getState());

    if (!_.isNil(userId) && !_.isNil(userIdFromState) && userIdFromState !== userId) {
        dispatch(onForbidden());
    } else {
        dispatch(fetchUpdateSubRoles(newSubRole));
    }
};

export const fetchUpdateSubRolesSuccess = createAction("FETCH_UPDATE_SUB_ROLES_SUCCESS");
export const fetchUpdateSubRolesFailed = createAction("FETCH_UPDATE_SUB_ROLES_FAILED");
export const onUpdateSubRolesSuccess = (newSubRole) => (userData) => (dispatch, getState) => {
    let subRoles = _.sortBy(userData.subRoles);
    let orgPermissions = getOrgPermissions(getState());
    if (!_.isNil(orgPermissions) && !orgPermissions.hasIVs) {
        subRoles = subRoles.filter((subRole) => {
            return !SUB_ROLES_REQUIRING_HAS_IVS.includes(subRole);
        });
    }
    if (subRoles.includes(newSubRole)) {
        let role = getCurrentRole(getState());
        if (role === ROLES.EMPLOYEE && newSubRole !== SUB_ROLES.ANCHOR && newSubRole !== NO_SUB_ROLES) {
            dispatch(fetchGetGroups(newSubRole));
        }
        StorageApi.setLastUsedSubRole(newSubRole);
        dispatch(setCurrentSubRole(newSubRole));
    } else {
        let oldSubRole = getCurrentSubRole(getState());
        if (!subRoles.includes(oldSubRole)) {
            dispatch(setCurrentSubRole(subRoles.length > 0 ? subRoles[0] : NO_SUB_ROLES));
        }
        dispatch(setUserSubRoleMissing(true));
    }
    dispatch(fetchUpdateSubRolesSuccess(subRoles));
};
export const fetchUpdateSubRoles = createAction(
    'FETCH_UPDATE_SUB_ROLES',
    () => Api.getUserData(),
    (newSubRole) => ({
        apiCall: true,
        onSuccess: onUpdateSubRolesSuccess(newSubRole),
        onFail: fetchUpdateSubRolesFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const changeCurrentGroup = (newGroupId) => (dispatch, getState) => {
    let userId = StorageApi.getUserId();
    let userIdFromState = getUserId(getState());

    if (!_.isNil(userId) && !_.isNil(userIdFromState) && userIdFromState !== userId) {
        dispatch(onForbidden());
    } else {
        StorageApi.setLastUsedGroup(newGroupId);
        dispatch(onChangeCurrentGroup(newGroupId));
        dispatch(cleanStartAccreditation());
    }
};

export const onChangeCurrentGroup = createAction('CURRENT_GROUP_CHANGE', newGroupId => newGroupId);

export const setUserSubRoleMissing = createAction('SET_USER_SUB_ROLE_MISSING');

export const setOrgPermissionsMissing = createAction('SET_ORG_PERMISSIONS_MISSING');

export const onChangeConsentRequestsFilterVisibility = createAction('CONSENT_REQUESTS_FILTER_VISIBILITY', isVisible => isVisible);

export const removeSubRolesRequiringHasIVs = () => (dispatch, getState) => {
    let subRoles = getSubRoles(getState());

    subRoles = subRoles.filter((subRole) => {
        return !SUB_ROLES_REQUIRING_HAS_IVS.includes(subRole);
    });

    dispatch(setSubRoles(subRoles));
};

//- State
const initialState = {
    role: null,
    subRoles: null,
    currentSubRole: null,
    currentGroup: null,
    groups: null,
    fetching: false,
    fetchingUpdateSubRoles: false,
    error: null,
    userSubRoleIsMissing: false,
    orgPermissionsMissing: false,
    isConsentRequestsFilterVisible: false
};

//- Reducers
export default handleActions({

    SET_USER_SUB_ROLE_MISSING: (state, action) => {
        return { ...state, userSubRoleIsMissing: action.payload };
    },

    SET_ORG_PERMISSIONS_MISSING: (state, action) => {
        return { ...state, orgPermissionsMissing: action.payload };
    },

    ROLE_SET: (state, action) => {
        return { ...state, role: ROLES[action.payload] };
    },

    FETCH_GET_SUB_ROLES: (state) => {
        return { ...state, error: null, fetching: true };
    },

    FETCH_GET_SUB_ROLES_FAILED: (state, action) => {
        return { ...state, subRoles: null, currentSubRole: null, error: action.payload, fetching: false };
    },

    FETCH_GET_SUB_ROLES_SUCCESS: (state) => {
        return { ...state, error: null, fetching: false };
    },

    SUB_ROLES_SET: (state, action) => {
        return { ...state, subRoles: action.payload };
    },

    FETCH_UPDATE_SUB_ROLES: (state) => {
        return { ...state, fetchingUpdateSubRoles: true };
    },

    FETCH_UPDATE_SUB_ROLES_SUCCESS: (state, action) => {
        return { ...state, subRoles: action.payload, fetchingUpdateSubRoles: false };
    },

    FETCH_UPDATE_SUB_ROLES_FAILED: (state, action) => {
        return { ...state, error: action.payload, fetchingUpdateSubRoles: false };
    },

    FETCH_GET_GROUPS: (state) => {
        return { ...state, groups: null, currentGroup: null, error: null, fetching: true };
    },

    FETCH_GET_GROUPS_FAILED: (state, action) => {
        return { ...state, error: action.payload, fetching: false };
    },

    FETCH_GET_GROUPS_SUCCESS: (state, action) => {
        return { ...state, groups: action.payload, error: null, fetching: false };
    },

    CURRENT_SUB_ROLE_SET: (state, action) => {
        return { ...state, currentSubRole: action.payload };
    },

    CURRENT_GROUP_CHANGE: (state, action) => {
        return { ...state, currentGroup: action.payload };
    },

    CONSENT_REQUESTS_FILTER_VISIBILITY: (state, action) => {
        return { ...state, isConsentRequestsFilterVisible: action.payload };
    }

}, initialState);

export const isUserSubRoleMissing = state => getRole(state).userSubRoleIsMissing;

export const isOrgPermissionsMissing = state => getRole(state).orgPermissionsMissing;

export const getRole = state => state.role;

export const getCurrentRole = state => getRole(state).role;

export const getCurrentSubRole = state => getRole(state).currentSubRole;

export const getSubRoles = state => getRole(state).subRoles;

export const getGroupsForSubRole = state => getRole(state).groups;

export const getCurrentGroup = state => getRole(state).currentGroup;

export const getLoading = state => getRole(state).fetching || getRole(state).fetchingUpdateSubRoles;

export const getGroupSelectionEnabling = state => getRole(state).isGroupSelectionEnable;

export const getConsentRequestsFilterVisibility = state => getRole(state).isConsentRequestsFilterVisible;

export const getCurrentGroupEmail = state => {
    let currentGroupId = getCurrentGroup(state);
    let groups = getGroupsForSubRole(state);

    let currentGroup = _.find(groups, group => group.id === currentGroupId);
    return _.isNil(currentGroup) ? null : currentGroup.email;
};

export const getCurrentGroupName = state => {
    let currentGroupId = getCurrentGroup(state);
    let groups = getGroupsForSubRole(state);

    let currentGroup = _.find(groups, group => group.id === currentGroupId);
    return _.isNil(currentGroup) ? null : currentGroup.name;
};

export const getGroupsNumberForSubRole = state => {
    let groups = getRole(state).groups;
    return groups ? groups.length : null;
};


// WEBPACK FOOTER //
// ./src/reducers/account/role.js